/* eslint-disable @typescript-eslint/no-explicit-any -- formulaire U2000 */
import { useCallback, useEffect, useMemo } from "react";
import type { TFunction } from "i18next";
import U2000InputText from "../../../../u2000Core/components/formControls/input/U2000InputText";
import U2000InputCurrency from "../../../../u2000Core/components/formControls/input/U2000InputCurrency";
import U2000InputCalendarCustom from "../../../../u2000Core/components/formControls/input/U2000InputCalendarCustom";
import U2000ComboBox from "../../../../u2000Core/components/formControls/combobox/U2000ComboBox";
import U2000Label from "../../../../u2000Core/components/formControls/label/U2000Label";
import U2000Panel from "../../../../u2000Core/components/layout/U2000Panel";
import U2000AutoCompleteInput from "../../../../u2000Core/components/formControls/autoComplete/U2000AutoCompleteInput";
import { U2000FormStateEnumEditMode, U2000FormStateEnumState } from "../../../../u2000Core/reducer/formReducer/U2000FormState";
import type { U2000FormStateHandlers } from "../../../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { U3129ProjectSearchPanel } from "../../../../u3129Timesheet/components/form/U3129LineProjectSubprojectRowCells";
import type { U3129FindProjectItem } from "../../../../u3129Timesheet/U3129Service";
import { NS, U3125ROLES, u3125FormatCurrency } from "../../../U3125";
import { useU2000AuthContext } from "../../../../u2000Core/context/auth/U2000UseAuthContext";
import {
  luHandleAppiError,
  pickInvoiceStatusLabel,
  pickInvoiceTaxesLabel,
  pickInvoiceTypeLabel,
  pickTaxRateLabel,
  taxRateOptionValue,
  u3125FilterStandardBillingInvoiceTypes,
  u3125FilterInvoiceTypesByDocumentNature,
  u3125FilterTaxRatesForInvoiceSelection,
  u3125FindTaxRateByCode,
  u3125FormatBillingFrequency,
  u3125FormatProjectBillingType,
  u3125FormatProjectHourlyMarkup,
  u3125InvoiceAmountsExceedRemainingErrors,
  u3125ShouldApplyInvoiceRemainingCapValidation,
  u3125InvoiceRequiresTaxRateCode,
  u3125IsAdvanceInvoice,
  u3125InvoiceDisplayTotals,
  u3125ResolveMontantsPanelTotals,
  u3125ProjectInvoicingStatAmount,
  u3125ProjectTecRemainingStat,
  u3125RecalcHeaderTaxAmounts,
  u3125SortTaxRateComponents,
  u3125SuggestInvoiceTypeCodeFromAmounts,
  u3125TaxAmountFieldLabels,
  u3125TaxRateCodeAllowed,
  u3125DocumentNatureUsesWipLines,
  u3125FormatInvoiceNumberDisplay,
  u3125IsWriteOffDocument,
  u3125ShouldShowAvailableCreditAlert,
  u3125CanOpenInvoicePdf,
  type U3125InvoiceFormRow,
  type U3125InvoiceStatusInitItem,
  type U3125InvoiceTypeInitItem,
  type U3125OriginalInvoiceCandidate,
  type U3125TaxRateInitItem,
} from "../../../U3125Service";
import type { useU3125FindProjectHandlers } from "../../../U3125UseFindProjectHandlers";
import { useU3125OpenInvoicePdfHandlers } from "../../../U3125UseApiHandlers";
import { useU3125Context } from "../../../U3125Context";
import U3125AvailableCreditAlertBanner from "./U3125AvailableCreditAlertBanner";
import U2000CheckBoxBase from "../../../../u2000Core/components/formControls/checkbox/U2000CheckBoxBase";
import U2000ActionBtn from "../../../../u2000Core/components/buttons/U2000ActionBtn";
import { FaFilePdf } from "react-icons/fa6";
import { useToast } from "../../../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../../../u2000Core/axios/U2000HandleApiError";

interface U3125FactureBodyProps {
  formStateHandlers: U2000FormStateHandlers<U3125InvoiceFormRow>;
  /** Ligne facture courante (synchronisée avec le reducer parent — affichage alerte BL-0061). */
  invoiceRow?: U3125InvoiceFormRow | null;
  t: TFunction;
  invoiceTypes: U3125InvoiceTypeInitItem[];
  invoiceStatuses: U3125InvoiceStatusInitItem[];
  taxRates: U3125TaxRateInitItem[];
  findProjectHandlers: ReturnType<typeof useU3125FindProjectHandlers>;
  i18nLanguage: string;
  routeParamId: string | undefined;
  projectLocked?: boolean;
  showReleaseAfterCreate?: boolean;
  releaseAfterCreate?: boolean;
  onReleaseAfterCreateChange?: (value: boolean) => void;
  releaseAfterCreateDisabled?: boolean;
  canRelease?: boolean;
}

function formatOriginalInvoiceLabel(c: U3125OriginalInvoiceCandidate): string {
  const num = u3125FormatInvoiceNumberDisplay(c.invoiceNumber);
  const typePart = (c.invoiceTypeLabel?.trim() || c.invoiceTypeCode || "").trim();
  const desc = c.description?.trim();
  if (desc && typePart) return `${num} — ${desc} (${typePart})`;
  if (typePart) return `${num} — ${typePart}`;
  return num;
}

function U3125ProjectReadonlyField({ label, value, textRight = false }: { label: string; value: string; textRight?: boolean }) {
  return (
    <div className="space-y-1.5">
      <U2000Label label={label} />
      <div
        className={`w-full cursor-not-allowed rounded-lg border border-gray-200 bg-muted px-4 py-3 text-sm text-gray-500${textRight ? " text-right" : ""}`}
      >
        {value || "\u00A0"}
      </div>
    </div>
  );
}

function U3125FactureBody({
  formStateHandlers,
  invoiceRow,
  t,
  invoiceTypes,
  invoiceStatuses: _invoiceStatuses,
  taxRates,
  findProjectHandlers,
  i18nLanguage,
  routeParamId,
  projectLocked = false,
  showReleaseAfterCreate = false,
  releaseAfterCreate = false,
  onReleaseAfterCreateChange,
  releaseAfterCreateDisabled = false,
  canRelease: _canRelease = true,
}: U3125FactureBodyProps) {
  const { hasRole } = useU2000AuthContext();
  const canViewTecLines = hasRole(U3125ROLES.VIEWTECLINES);
  const readonly = formStateHandlers.getFormState() === U2000FormStateEnumState.READ;
  const isAdd = formStateHandlers.getFormEditMode() === U2000FormStateEnumEditMode.ADD;
  const data = invoiceRow ?? formStateHandlers.getData();
  const toast = useToast();
  const { luStateHandlers } = useU3125Context();
  const enableInvoicePdfAndLines = Boolean(
    (luStateHandlers.getPartialPersonnalization(["enableInvoicePdfAndLines"]) as { enableInvoicePdfAndLines?: boolean } | undefined)
      ?.enableInvoicePdfAndLines,
  );
  const allowInvoiceAboveBudget = Boolean(
    (luStateHandlers.getPartialPersonnalization(["allowInvoiceAboveBudget"]) as { allowInvoiceAboveBudget?: boolean } | undefined)
      ?.allowInvoiceAboveBudget,
  );

  const openInvoicePdfHandlers = useU3125OpenInvoicePdfHandlers({
    onPopupBlocked: () => toast.warning(t("msgPdfNewTabBlocked", { ns: NS }), t("lblWarningsTitle", { ns: NS })),
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const activeTaxRates = useMemo(() => taxRates.filter((r) => r.active), [taxRates]);
  const documentNature = data?.documentNature ?? "STANDARD";
  const isWriteOff = u3125IsWriteOffDocument(documentNature);
  const showAvailableCreditAlert = u3125ShouldShowAvailableCreditAlert(data);
  const originalCandidates = data?.originalInvoiceCandidates ?? [];
  const showOriginalInvoice =
    (documentNature === "CREDIT" || documentNature === "ADVANCE") && originalCandidates.length > 0;
  const selectableInvoiceTypes = useMemo(() => {
    const byNature = u3125FilterInvoiceTypesByDocumentNature(invoiceTypes, documentNature);
    if (documentNature === "STANDARD" && isAdd && !showOriginalInvoice) {
      const billingTypes = u3125FilterStandardBillingInvoiceTypes(byNature);
      if (billingTypes.length > 0) return billingTypes;
    }
    return byNature;
  }, [documentNature, isAdd, showOriginalInvoice, invoiceTypes]);

  const filteredTaxRates = useMemo(
    () =>
      u3125FilterTaxRatesForInvoiceSelection(activeTaxRates, {
        customerTaxable: data?.customerTaxable,
        invoiceCountryId: data?.country?.id,
        invoiceProvinceId: data?.province?.id,
      }),
    [activeTaxRates, data?.customerTaxable, data?.country?.id, data?.province?.id],
  );

  const subtotal = (data?.amountInvoiceTaxable ?? 0) + (data?.amountInvoiceNonTaxable ?? 0);
  const displayTotals = useMemo(() => {
    if (!data) return { subtotal: 0, taxAmount1: 0, taxAmount2: 0, total: 0 };
    return u3125InvoiceDisplayTotals(data, activeTaxRates);
  }, [
    data,
    activeTaxRates,
  ]);
  const montantsTotals = useMemo(() => {
    if (!data || documentNature !== "STANDARD") return null;
    return u3125ResolveMontantsPanelTotals(data, activeTaxRates, invoiceTypes);
  }, [data, documentNature, activeTaxRates, invoiceTypes]);
  const displaySubtotal = montantsTotals?.subtotal ?? (documentNature === "STANDARD" ? displayTotals.subtotal : subtotal);
  const billingTotals = montantsTotals;
  const invoiceTotal = billingTotals?.invoiceTotal ?? (documentNature === "STANDARD" ? displayTotals.total : subtotal + (data?.taxAmount1 ?? 0) + (data?.taxAmount2 ?? 0));
  const advanceApplicationsTotal = billingTotals?.advanceApplicationsTotal ?? 0;
  const grandTotal = billingTotals?.grandTotal ?? invoiceTotal;
  const creditAffectsMontants = (billingTotals?.creditApplicationsBeforeTaxTotal ?? 0) > 0;
  const showStandardBillingFooter = documentNature === "STANDARD";
  const usesWipLines = u3125DocumentNatureUsesWipLines(documentNature);
  const isAdvanceInvoice = u3125IsAdvanceInvoice(
    data ?? { documentNature: "STANDARD", invoiceTypeCode: "" },
    invoiceTypes,
  );
  const showProjectInvoicingStats = !isWriteOff && data?.project?.fixedPrice != null;
  const showTaxFields = !isWriteOff && documentNature !== "ADVANCE" && !isAdvanceInvoice;
  const showTaxableAmountField = !isAdvanceInvoice;

  const amountGridClass = usesWipLines ? "md:grid-cols-3" : showTaxableAmountField ? "md:grid-cols-2" : "md:grid-cols-1";
  const projectInvoicingStatAmount = useMemo(
    () =>
      u3125ProjectInvoicingStatAmount({
        fixedPrice: data?.project?.fixedPrice,
        fixedAmount: data?.project?.fixedAmount,
        invoiceAmount: data?.invoiceAmount,
      }),
    [data?.project?.fixedPrice, data?.project?.fixedAmount, data?.invoiceAmount],
  );
  const projectInvoicingStatLabelKey =
    documentNature === "CREDIT" && data?.project?.fixedPrice === true
      ? "lblRemainingToCreditShort"
      : documentNature === "ADVANCE" && data?.project?.fixedPrice === true
        ? "lblRemainingToAdvanceShort"
        : data?.project?.fixedPrice === true
          ? "lblRemainingToInvoiceShort"
          : "lblUnbilledWipSellingShort";

  const invoicePanelTitle = isWriteOff
    ? t("sectionWriteOff", { ns: NS })
    : documentNature === "CREDIT"
      ? t("sectionCreditNote", { ns: NS })
      : documentNature === "ADVANCE"
        ? t("sectionAdvanceNote", { ns: NS })
        : t("sectionInvoice", { ns: NS });
  const taxableAmountLabel =
    documentNature === "CREDIT"
      ? t("lblAmountCreditTaxable", { ns: NS })
      : documentNature === "ADVANCE"
        ? t("lblAmountAdvanceTaxable", { ns: NS })
        : t("lblAmountInvoiceTaxable", { ns: NS });
  const nonTaxableAmountLabel =
    documentNature === "CREDIT"
      ? t("lblAmountCreditNonTaxable", { ns: NS })
      : documentNature === "ADVANCE"
        ? t("lblAmountAdvanceNonTaxable", { ns: NS })
        : t("lblAmountInvoiceNonTaxable", { ns: NS });

  const projectTecRemainingStat = useMemo(
    () => u3125ProjectTecRemainingStat({ wipLines: data?.workInProgressLines }),
    [data?.workInProgressLines],
  );

  const renderAmountFieldColumn = (
    fieldName: "amountTec" | "amountInvoiceTaxable" | "amountInvoiceNonTaxable",
    label: string,
    options?: { statText?: string | null; fieldReadonly?: boolean; fieldDisabled?: boolean },
  ) => (
    <div className="space-y-1.5">
      <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5">
        <label htmlFor={fieldName} className="shrink-0 text-sm font-medium text-gray-700 select-none">
          {label}
        </label>
        {options?.statText ? (
          <span className="min-w-0 wrap-break-word text-right text-xs font-medium leading-snug text-red-600">
            {options.statText}
          </span>
        ) : null}
      </div>
      <U2000InputCurrency
        formStateHandlers={formStateHandlers as any}
        fieldName={fieldName}
        label=""
        readonly={options?.fieldReadonly ?? readonly}
        disabled={options?.fieldDisabled ?? false}
        decimalDigits={2}
        allowNegative
      />
    </div>
  );

  const amountTecLabel = t(data?.project?.fixedPrice === false ? "lblAmountTecCost" : "lblAmountTec", { ns: NS });
  const amountTecStatText =
    canViewTecLines && showProjectInvoicingStats && projectTecRemainingStat != null
      ? t("lblTecRemainingToInvoiceShort", {
          amount: u3125FormatCurrency(projectTecRemainingStat.amount),
          count: projectTecRemainingStat.count,
          ns: NS,
        })
      : null;

  const projectInvoicingStatText =
    showProjectInvoicingStats && projectInvoicingStatAmount != null
      ? t(projectInvoicingStatLabelKey, {
          amount: u3125FormatCurrency(projectInvoicingStatAmount),
          remaining: u3125FormatCurrency(projectInvoicingStatAmount),
          count: data?.invoiceCount ?? 0,
          ns: NS,
        })
      : null;

  const renderTaxAmountField = (fieldName: "taxAmount1" | "taxAmount2", label: string) => (
    <div key={`${fieldName}-${data?.taxRateCode ?? ""}`} className="space-y-1.5">
      <U2000Label label={label} />
      <U2000InputCurrency formStateHandlers={formStateHandlers as any} fieldName={fieldName} label="" readonly decimalDigits={2} allowNegative />
    </div>
  );

  const renderTaxAmountDisplay = (label: string, amount: number) => (
    <div key={`${label}-${data?.taxRateCode ?? ""}`} className="space-y-1.5">
      <U2000Label label={label} />
      <div className="rounded-lg border border-gray-200 bg-muted px-4 py-3 text-right text-sm font-medium">{u3125FormatCurrency(amount)}</div>
    </div>
  );

  const onSearchProject = useCallback(
    async (criterias: { fieldName: string; value: unknown }[]) => findProjectHandlers.search(criterias),
    [findProjectHandlers],
  );

  const extractProject = useCallback(
    (result: U3129FindProjectItem | null): { fieldName: string; value: unknown }[] => {
      if (result) {
        return [
          { fieldName: "project", value: { id: result.id, projetNumber: result.projetNumber, projetName: result.projetName } },
          { fieldName: "workInProgressLineIds", value: [] },
        ];
      }
      return [
        { fieldName: "project", value: null },
        { fieldName: "workInProgressLineIds", value: [] },
      ];
    },
    [],
  );

  useEffect(() => {
    if (readonly) return;
    const row = formStateHandlers.getData();
    if (!row) return;
    if (row.customerTaxable === false && row.taxRateCode?.trim()) {
      formStateHandlers.setData({ ...row, taxRateCode: "" });
    }
  }, [readonly, data?.customerTaxable, formStateHandlers]);

  useEffect(() => {
    if (readonly) return;
    const row = formStateHandlers.getData();
    if (!row) return;
    const isAdvance = u3125IsAdvanceInvoice(row, invoiceTypes);
    const isNonTaxableCredit = row.documentNature === "CREDIT" && row.customerTaxable === false;
    if (!isAdvance && !isNonTaxableCredit) return;
    if (Number(row.amountInvoiceTaxable ?? 0) === 0 && row.taxAmount1 == null && row.taxAmount2 == null) return;
    formStateHandlers.setData({
      ...row,
      amountInvoiceTaxable: 0,
      taxAmount1: null,
      taxAmount2: null,
    });
  }, [readonly, invoiceTypes, data?.documentNature, data?.invoiceTypeCode, data?.customerTaxable, data?.amountInvoiceTaxable, data?.taxAmount1, data?.taxAmount2, formStateHandlers]);

  useEffect(() => {
    if (readonly) return;
    const row = formStateHandlers.getData();
    if (!row?.taxRateCode?.trim()) return;
    if (u3125IsAdvanceInvoice(row, invoiceTypes)) return;
    if (!u3125TaxRateCodeAllowed(row.taxRateCode, filteredTaxRates)) {
      formStateHandlers.setData({ ...row, taxRateCode: "" });
    }
  }, [readonly, filteredTaxRates, invoiceTypes, data?.documentNature, data?.invoiceTypeCode, data?.taxRateCode, formStateHandlers]);

  useEffect(() => {
    if (readonly) return;
    const row = formStateHandlers.getData();
    if (!row) return;
    if (!u3125InvoiceRequiresTaxRateCode(row, invoiceTypes)) {
      const errors = formStateHandlers.getErrors();
      if (errors.taxRateCode) {
        const { taxRateCode: _ignored, ...rest } = errors;
        formStateHandlers.setErrors(rest);
      }
      return;
    }
    if (row.taxRateCode?.trim()) return;
    if (filteredTaxRates.length !== 1) return;
    const onlyValue = taxRateOptionValue(filteredTaxRates[0]);
    if (!onlyValue) return;
    formStateHandlers.setData({ ...row, taxRateCode: onlyValue });
  }, [filteredTaxRates, data?.taxRateCode, data?.customerTaxable, data?.documentNature, data?.invoiceTypeCode, readonly, invoiceTypes, formStateHandlers]);

  useEffect(() => {
    if (readonly) return;
    const row = formStateHandlers.getData();
    if (!row || u3125IsAdvanceInvoice(row, invoiceTypes)) return;
    const taxes = u3125RecalcHeaderTaxAmounts(row, taxRates);
    if (row.taxAmount1 === taxes.taxAmount1 && row.taxAmount2 === taxes.taxAmount2) return;
    formStateHandlers.setData({ ...row, taxAmount1: taxes.taxAmount1, taxAmount2: taxes.taxAmount2 });
  }, [readonly, data?.taxRateCode, data?.amountInvoiceTaxable, data?.customerTaxable, data?.documentNature, taxRates, formStateHandlers]);

  useEffect(() => {
    if (readonly) return;
    const row = formStateHandlers.getData();
    if (!row) return;
    const suggested = u3125SuggestInvoiceTypeCodeFromAmounts(row, invoiceTypes);
    if (!suggested || row.invoiceTypeCode === suggested) return;
    if (row.documentNature !== "STANDARD") return;
    formStateHandlers.setData({ ...row, invoiceTypeCode: suggested });
  }, [
    readonly,
    invoiceTypes,
    data?.amountInvoiceTaxable,
    data?.amountInvoiceNonTaxable,
    data?.project?.fixedPrice,
    data?.project?.fixedAmount,
    data?.invoiceAmount,
    data?.workInProgressLines,
    data?.documentNature,
    formStateHandlers,
  ]);

  useEffect(() => {
    if (readonly) return;
    const row = formStateHandlers.getData();
    if (!row) return;
    if (u3125IsWriteOffDocument(row.documentNature)) return;

    const currentErrors = { ...formStateHandlers.getErrors() };
    const msg = t("errInvoiceAmountsExceedRemaining", { ns: NS });
    if (!u3125ShouldApplyInvoiceRemainingCapValidation(allowInvoiceAboveBudget, row.documentNature)) {
      const clearedErrors = { ...currentErrors };
      let cleared = false;
      if (clearedErrors.amountInvoiceTaxable === msg) {
        delete clearedErrors.amountInvoiceTaxable;
        cleared = true;
      }
      if (clearedErrors.amountInvoiceNonTaxable === msg) {
        delete clearedErrors.amountInvoiceNonTaxable;
        cleared = true;
      }
      if (cleared) {
        formStateHandlers.setErrors(clearedErrors);
      }
      return;
    }
    const exceedErrors = u3125InvoiceAmountsExceedRemainingErrors(row, msg);
    if (Object.keys(exceedErrors).length > 0) {
      const hasChange = Object.entries(exceedErrors).some(([field, error]) => currentErrors[field as keyof typeof currentErrors] !== error);
      if (hasChange) {
        formStateHandlers.setErrors({ ...currentErrors, ...exceedErrors });
      }
      return;
    }
    const clearedErrors = { ...currentErrors };
    let cleared = false;
    if (clearedErrors.amountInvoiceTaxable === msg) {
      delete clearedErrors.amountInvoiceTaxable;
      cleared = true;
    }
    if (clearedErrors.amountInvoiceNonTaxable === msg) {
      delete clearedErrors.amountInvoiceNonTaxable;
      cleared = true;
    }
    if (cleared) {
      formStateHandlers.setErrors(clearedErrors);
    }
  }, [
    readonly,
    allowInvoiceAboveBudget,
    data?.documentNature,
    data?.amountInvoiceTaxable,
    data?.amountInvoiceNonTaxable,
    data?.project?.fixedPrice,
    data?.project?.fixedAmount,
    data?.invoiceAmount,
    data?.workInProgressLines,
    formStateHandlers,
    t,
  ]);

  const handleOpenPdfInNewTab = async () => {
    if (!routeParamId || routeParamId === "new") return;
    await openInvoicePdfHandlers.openPdfInNewTab(routeParamId);
  };

  const canOpenPdfInNewTab = u3125CanOpenInvoicePdf(enableInvoicePdfAndLines, isWriteOff, data?.status);
  const customerNotTaxable = data?.customerTaxable === false;
  const taxRateRequired = u3125InvoiceRequiresTaxRateCode(data ?? { customerTaxable: true, documentNature: "STANDARD", invoiceTypeCode: "" }, invoiceTypes);
  const taxSelectDisabled = readonly || customerNotTaxable || isAdvanceInvoice || isWriteOff;

  const activeTaxRate = useMemo(() => {
    if (customerNotTaxable) return undefined;
    const code = data?.taxRateCode?.trim();
    if (!code) return undefined;
    return u3125FindTaxRateByCode(taxRates, code);
  }, [customerNotTaxable, data?.taxRateCode, taxRates]);

  const activeTaxComponents = useMemo(
    () => u3125SortTaxRateComponents(activeTaxRate?.taxRateComponents ?? []),
    [activeTaxRate],
  );
  const projectReadonly = readonly || (isAdd && projectLocked && !!data?.project?.id);

  const readonlyTaxRateLabel = useMemo(() => {
    const taxes = data?.taxes ?? null;
    const code = (taxes?.code?.trim() || data?.taxRateCode?.trim()) ?? "";
    if (!code) return "";
    const labelFromTaxes = pickInvoiceTaxesLabel(taxes, i18nLanguage);
    if (labelFromTaxes) return `${labelFromTaxes} (${code})`;
    const found = taxRates.find((r) => taxRateOptionValue(r) === code);
    return found ? `${pickTaxRateLabel(found, i18nLanguage)} (${code})` : code;
  }, [data?.taxes, data?.taxRateCode, taxRates, i18nLanguage]);

  const taxAmountLabels = useMemo(
    () =>
      u3125TaxAmountFieldLabels(taxRates, data?.taxRateCode, i18nLanguage, {
        taxAmount1: t("lblTaxAmount1", { ns: NS }),
        taxAmount2: t("lblTaxAmount2", { ns: NS }),
      }),
    [taxRates, data?.taxRateCode, i18nLanguage, t],
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="mb-1 flex flex-wrap gap-2">
        {readonly && routeParamId && routeParamId !== "new" && canOpenPdfInNewTab ? (
          <U2000ActionBtn label={t("btnOpenPdfNewTab", { ns: NS })} icon={FaFilePdf} handleClick={() => void handleOpenPdfInNewTab()} disabled={false} />
        ) : null}
      </div>
      {showAvailableCreditAlert && data?.availableCreditAlert ? (
        <U3125AvailableCreditAlertBanner alert={data.availableCreditAlert} t={t} />
      ) : null}
      {customerNotTaxable && !isWriteOff ? (
        <p className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3.5 text-sm text-amber-900">{t("msgCustomerNotTaxable", { ns: NS })}</p>
      ) : null}
      {isWriteOff ? (
        <p className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3.5 text-sm text-slate-800">{t("msgWriteOffInternalDocument", { ns: NS })}</p>
      ) : null}

      <div className="grid grid-cols-1 items-stretch gap-6 xl:grid-cols-[1fr_minmax(240px,280px)]">
        <div className="flex h-full min-h-0 flex-col gap-6">
          <U2000Panel title={t("sectionProject", { ns: NS })}>
            {!projectReadonly ? (
              <div className="mb-4">
                <U2000AutoCompleteInput
                  pageSize={15}
                  formStateHandlers={formStateHandlers as any}
                  fieldName="project"
                  mainFieldName="projetNumber"
                  entityLink="U3121"
                  label={t("lblProject", { ns: NS })}
                  onSearch={onSearchProject}
                  displayFn={(item) => {
                    const p = item as U3129FindProjectItem | null;
                    return p ? (p.projetNumber ?? p.id) : "";
                  }}
                  overrideExtractDataFn={(item) => extractProject(item as U3129FindProjectItem | null)}
                  basicResultHeight={300}
                  resultLabels={[]}
                  resultFields={["projetNumber", "projetName"]}
                  minCarLaunchSearch={3}
                  customSearchPanelHeight={500}
                  panelWidth={400}
                  dropdownWidth={400}
                  renderPanel={(props) => <U3129ProjectSearchPanel {...props} ns="u3129" />}
                  readonly={readonly}
                />
              </div>
            ) : null}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <U3125ProjectReadonlyField label={t("lblProjectNumber", { ns: NS })} value={data?.project?.projetNumber?.trim() ?? ""} />
              <U3125ProjectReadonlyField label={t("lblProjetName", { ns: NS })} value={data?.project?.projetName?.trim() ?? ""} />
              <U3125ProjectReadonlyField
                label={t("lblProjectType", { ns: NS })}
                value={u3125FormatProjectBillingType(data?.project?.fixedPrice, t, NS)}
              />
              <U3125ProjectReadonlyField
                label={data?.project?.fixedPrice === false ? t("lblSellingRate", { ns: NS }) : t("lblFixedAmount", { ns: NS })}
                textRight
                value={
                  data?.project?.fixedPrice === false
                    ? data?.project?.sellingRate != null
                      ? u3125FormatCurrency(data.project.sellingRate)
                      : ""
                    : data?.project?.fixedAmount != null
                      ? u3125FormatCurrency(data.project.fixedAmount)
                      : ""
                }
              />
              <U3125ProjectReadonlyField
                label={t("lblBillingFrequency", { ns: NS })}
                value={u3125FormatBillingFrequency(data?.project?.billingFrequency, t, NS)}
              />
              <U3125ProjectReadonlyField
                label={t("lblHourlyMarkup", { ns: NS })}
                textRight
                value={u3125FormatProjectHourlyMarkup(data?.project?.hourlyMarkup)}
              />
            </div>
          </U2000Panel>

          <U2000Panel title={invoicePanelTitle} className="flex min-h-0 flex-1 flex-col">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <div className="space-y-1.5 md:col-span-1">
                <U2000Label label={t("lblInvoiceNumber", { ns: NS })} />
                <div className="w-full cursor-not-allowed rounded-lg border border-gray-200 bg-muted px-4 py-3 text-sm text-gray-500">
                  {u3125FormatInvoiceNumberDisplay(data?.invoiceNumber, t("lblInvoiceNumberAutoHint", { ns: NS }))}
                </div>
              </div>
              <U2000InputCalendarCustom
                formStateHandlers={formStateHandlers as any}
                fieldName="invoiceDate"
                label={t("lblInvoiceDate", { ns: NS })}
                readonly={readonly}
                required
                dateFormat="yyyy-MM-dd"
              />
              <div className="space-y-1.5 md:col-span-2 lg:col-span-1">
                <U2000Label label={t("lblInvoiceStatus", { ns: NS })} />
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <div className="min-w-0 sm:max-w-48 sm:shrink-0">
                    <div className="w-full cursor-not-allowed rounded-lg border border-gray-200 bg-muted px-4 py-3 text-sm text-gray-500">
                      {pickInvoiceStatusLabel(t, data?.status ?? "OPEN")}
                    </div>
                  </div>
                  {showReleaseAfterCreate ? (
                    <div className="min-w-0 flex-1" title={t("lblReleaseAfterCreateTooltip", { ns: NS })}>
                      <U2000CheckBoxBase
                        fieldName="releaseAfterCreate"
                        label={t("lblReleaseAfterCreate", { ns: NS })}
                        value={releaseAfterCreate}
                        setValue={(v) => onReleaseAfterCreateChange?.(v)}
                        readonly={readonly || releaseAfterCreateDisabled}
                      />
                    </div>
                  ) : null}
                </div>
                {showReleaseAfterCreate && releaseAfterCreateDisabled ? (
                  <p className="text-sm text-gray-500">{t("errWizardReleaseRequiresEdit", { ns: NS })}</p>
                ) : null}
              </div>

              <U2000ComboBox
                formStateHandlers={formStateHandlers as any}
                fieldName="invoiceTypeCode"
                label={t("lblInvoiceTypeCode", { ns: NS })}
                data={selectableInvoiceTypes}
                keyField="code"
                descField="code"
                readonly={readonly}
                noneItemLabel={t("lblSelect", { ns: NS })}
                getValue={(it) => it.code}
                findItemByValue={(value, list) => {
                  if (value == null || value === "") return null;
                  return list.find((it) => it.code === String(value)) ?? null;
                }}
                formatLabel={(it) => `${pickInvoiceTypeLabel(invoiceTypes, it.code, i18nLanguage)} (${it.code})`}
              />
              {showTaxFields ? (
                readonly ? (
                  <div className="space-y-1.5">
                    <U2000Label label={t("lblTaxRateCode", { ns: NS })} />
                    <div className="w-full cursor-not-allowed rounded-lg border border-gray-200 bg-muted px-4 py-3 text-sm text-gray-500">{readonlyTaxRateLabel || "\u00A0"}</div>
                  </div>
                ) : activeTaxRates.length > 0 ? (
                  <U2000ComboBox
                    formStateHandlers={formStateHandlers as any}
                    fieldName="taxRateCode"
                    label={t("lblTaxRateCode", { ns: NS })}
                    data={filteredTaxRates}
                    keyField="id"
                    descField="code"
                    required={taxRateRequired}
                    readonly={taxSelectDisabled}
                    noneItemLabel={t("lblSelect", { ns: NS })}
                    getValue={(rate) => taxRateOptionValue(rate)}
                    findItemByValue={(value, list) => {
                      if (value == null || value === "") return null;
                      const v = String(value);
                      return list.find((rate) => taxRateOptionValue(rate) === v) ?? null;
                    }}
                    formatLabel={(rate) => {
                      const v = taxRateOptionValue(rate);
                      return `${pickTaxRateLabel(rate, i18nLanguage)} (${v})`;
                    }}
                  />
                ) : (
                  <U2000InputText formStateHandlers={formStateHandlers as any} fieldName="taxRateCode" label={t("lblTaxRateCode", { ns: NS })} readonly={readonly} required={false} />
                )
              ) : (
                <div className="hidden md:block" aria-hidden />
              )}
              {showOriginalInvoice ? (
                <U2000ComboBox
                  formStateHandlers={formStateHandlers as any}
                  fieldName="originalInvoiceId"
                  label={t("lblOriginalInvoice", { ns: NS })}
                  data={originalCandidates}
                  keyField="id"
                  descField="description"
                  readonly={readonly}
                  noneItemLabel={t("lblSelect", { ns: NS })}
                  getValue={(c) => c.id}
                  findItemByValue={(value, list) => list.find((c) => c.id === value) ?? null}
                  formatLabel={(c) => formatOriginalInvoiceLabel(c)}
                />
              ) : (
                <div className="hidden md:block" aria-hidden />
              )}

              <div className={`grid grid-cols-1 gap-4 md:col-span-3 ${amountGridClass}`}>
                {usesWipLines
                  ? renderAmountFieldColumn("amountTec", amountTecLabel, {
                      fieldReadonly: true,
                      statText: amountTecStatText,
                    })
                  : null}
                {showTaxableAmountField
                  ? renderAmountFieldColumn("amountInvoiceTaxable", taxableAmountLabel, {
                      fieldReadonly: isWriteOff || readonly,
                      fieldDisabled: !isWriteOff && customerNotTaxable && showTaxableAmountField,
                      statText: isWriteOff ? null : projectInvoicingStatText,
                    })
                  : null}
                {renderAmountFieldColumn("amountInvoiceNonTaxable", nonTaxableAmountLabel, {
                  fieldReadonly: isWriteOff || readonly,
                  statText: isWriteOff ? null : projectInvoicingStatText,
                })}
              </div>

              <div className="md:col-span-3">
                <U2000InputText formStateHandlers={formStateHandlers as any} fieldName="remark" label={t("lblRemark", { ns: NS })} readonly={readonly} required={false} />
              </div>
            </div>
          </U2000Panel>
        </div>

        <U2000Panel title={t("sectionAmounts", { ns: NS })} className="flex h-full flex-col">
          <div className="flex flex-col gap-4">
            {showStandardBillingFooter ? (
              <div className="space-y-1.5">
                <U2000Label label={t("lblCreditApplicationsBeforeTaxTotal", { ns: NS })} />
                <div className="rounded-lg border border-gray-200 bg-muted px-4 py-3 text-right text-sm font-medium">
                  {u3125FormatCurrency(billingTotals?.creditApplicationsBeforeTaxTotal ?? 0)}
                </div>
              </div>
            ) : null}
            <div className="space-y-1.5">
              <U2000Label label={t("lblSubtotal", { ns: NS })} />
              <div className="rounded-lg border border-gray-200 bg-muted px-4 py-3 text-right text-sm font-medium">{u3125FormatCurrency(displaySubtotal)}</div>
            </div>
            {showTaxFields && activeTaxComponents[0]
              ? creditAffectsMontants
                ? renderTaxAmountDisplay(taxAmountLabels.taxAmount1, billingTotals?.taxAmount1 ?? 0)
                : renderTaxAmountField("taxAmount1", taxAmountLabels.taxAmount1)
              : null}
            {showTaxFields && activeTaxComponents[1]
              ? creditAffectsMontants
                ? renderTaxAmountDisplay(taxAmountLabels.taxAmount2, billingTotals?.taxAmount2 ?? 0)
                : renderTaxAmountField("taxAmount2", taxAmountLabels.taxAmount2)
              : null}
            <div className="space-y-1.5">
              <U2000Label label={t("lblInvoiceTotalBilling", { ns: NS })} />
              <div className="rounded-lg border border-gray-200 bg-muted px-4 py-3 text-right text-sm font-semibold">{u3125FormatCurrency(invoiceTotal)}</div>
            </div>
            {showStandardBillingFooter ? (
              <div className="space-y-1.5">
                <U2000Label label={t("lblAdvanceApplicationsTotal", { ns: NS })} />
                <div className="rounded-lg border border-gray-200 bg-muted px-4 py-3 text-right text-sm font-medium">
                  {u3125FormatCurrency(advanceApplicationsTotal)}
                </div>
              </div>
            ) : null}
            {showStandardBillingFooter ? (
              <div className="space-y-1.5">
                <U2000Label label={t("lblGrandTotal", { ns: NS })} />
                <div className="rounded-lg border-2 border-primary/30 bg-muted px-4 py-3 text-right text-base font-bold">{u3125FormatCurrency(grandTotal)}</div>
              </div>
            ) : null}
          </div>
        </U2000Panel>
      </div>
    </div>
  );
};

export default U3125FactureBody;